import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Callout, Chip, Dialog, Skeleton, useToast } from './ui.tsx';
import { ApiError, get, post } from '../lib/api.ts';
import { date } from '../lib/format.ts';

export interface GxMailView {
  configured: boolean;
  mailbox: null | { address: string; status: 'provisioning' | 'active' | 'deactivated'; deactivatedAt: string | null; leftOn: string | null; deletesOn: string | null; lastError: string | null };
}

const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

/**
 * The GX Mail line of a profile: the person's address next to their sign-in e-mail, and HR's
 * actions on it. Hearth creates the mailbox in GX Mail before the address is used anywhere.
 */
export function GxMailRow({ employeeId, name, signIn, view, canEdit, hasLeft }: { employeeId: string; name: string; signIn: string | null; view: GxMailView; canEdit: boolean; hasLeft: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const mb = view.mailbox;
  const done = (title: string) => {
    void qc.invalidateQueries({ queryKey: ['person', employeeId] });
    toast({ title });
  };
  const finish = useMutation({
    mutationFn: () => post<{ address: string }>(`/api/people/${employeeId}/gx-mail`, { address: mb!.address }),
    onSuccess: (r) => done(`${r.address} is ready`),
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: ['person', employeeId] });
      toast({ title: err(e), tone: 'error' });
    },
  });
  const reactivate = useMutation({
    mutationFn: () => post<{ address: string }>(`/api/people/${employeeId}/gx-mail/reactivate`),
    onSuccess: (r) => done(`${r.address} is active again`),
    onError: (e) => toast({ title: err(e), tone: 'error' }),
  });
  if (!view.configured && !mb) return null;
  return (
    <>
      <dt>GX Mail</dt>
      <dd>
        {mb ? (
          <span className="row row--wrap" style={{ gap: 8 }}>
            <span className="mono">{mb.address}</span>
            {mb.status === 'provisioning' ? <Chip tone="warn">Not ready</Chip> : null}
            {mb.status === 'deactivated' ? <Chip tone="bad">Deactivated</Chip> : null}
            {mb.status === 'provisioning' && canEdit ? (
              <Button size="sm" variant="ghost" icon="refresh" loading={finish.isPending} onClick={() => finish.mutate()}>
                Finish
              </Button>
            ) : null}
            {mb.status === 'deactivated' && canEdit && !hasLeft ? (
              <Button size="sm" variant="ghost" icon="refresh" loading={reactivate.isPending} onClick={() => reactivate.mutate()}>
                Reactivate
              </Button>
            ) : null}
          </span>
        ) : canEdit && !hasLeft ? (
          <Button size="sm" variant="ghost" icon="inbox" onClick={() => setCreating(true)}>
            Create address
          </Button>
        ) : (
          '—'
        )}
        {mb?.status === 'deactivated' && mb.deletesOn ? <div className="faint" style={{ fontSize: 'var(--step--2)' }}>Left on {date(mb.leftOn)}; the mailbox and its mail are deleted on {date(mb.deletesOn)}.</div> : null}
        {mb?.lastError ? <div className="faint" style={{ fontSize: 'var(--step--2)' }}>{mb.lastError}</div> : null}
      </dd>
      <CreateDialog open={creating} onClose={() => setCreating(false)} employeeId={employeeId} name={name} signIn={signIn} onCreated={(a) => done(`${a} is ready`)} />
    </>
  );
}

function CreateDialog({ open, onClose, employeeId, name, signIn, onCreated }: { open: boolean; onClose: () => void; employeeId: string; name: string; signIn: string | null; onCreated: (address: string) => void }) {
  const qc = useQueryClient();
  // Asks GX Mail which address is free, every time the dialog opens.
  const s = useQuery({ queryKey: ['gx-mail-suggestion', employeeId], queryFn: () => get<{ address: string }>(`/api/people/${employeeId}/gx-mail/suggestion`), enabled: open, gcTime: 0, staleTime: 0, retry: false });
  const m = useMutation({
    mutationFn: () => post<{ address: string }>(`/api/people/${employeeId}/gx-mail`, { address: s.data!.address }),
    onSuccess: (r) => {
      onClose();
      onCreated(r.address);
    },
    onError: (e) => {
      // Taken meanwhile, or held after GX Mail did not answer: show what is true now.
      void s.refetch();
      void qc.invalidateQueries({ queryKey: ['person', employeeId] });
      if (e instanceof ApiError && e.status === 503) onClose();
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="GX Mail"
      title={
        <>
          A mailbox for <em className="serif-italic">{name}</em>
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!s.data} onClick={() => m.mutate()}>
            Create mailbox
          </Button>
        </>
      }
    >
      <div className="stack">
        {m.error ? <Callout tone="bad">{err(m.error)}</Callout> : null}
        {s.isError ? (
          <Callout tone="bad" title="No address yet">
            {err(s.error)}{' '}
            <Button size="sm" variant="ghost" onClick={() => void s.refetch()}>
              Try again
            </Button>
          </Callout>
        ) : s.data ? (
          <input className="input input--mono" readOnly value={s.data.address} aria-label="GX Mail address" onFocus={(e) => e.currentTarget.select()} />
        ) : (
          <Skeleton h={40} />
        )}
        <p className="muted">
          Hearth creates this mailbox in GX Mail (5 GiB) first, and only then starts using the address. {name} opens it by signing in with their Hearth account; phones and Outlook use app passwords
          made in GX Mail.
        </p>
        <p className="muted">
          {signIn ? <>Their Hearth sign-in stays <span className="mono">{signIn}</span>. </> : null}Payslip and other notices from Hearth go to the new address. Password links never do: they go to the sign-in or private e-mail, or you hand them over.
        </p>
      </div>
    </Dialog>
  );
}

/** A set-up link HR hands over in person (no sign-in or private e-mail outside GX Mail to send it to). */
export function SetupLinkDialog({ link, name, onClose }: { link: string | null; name: string; onClose: () => void }) {
  return (
    <Dialog
      open={!!link}
      onClose={onClose}
      eyebrow="Hand over in person"
      title={
        <>
          Set-up <em className="serif-italic">link</em>
        </>
      }
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="stack">
        <p className="muted">
          There is no e-mail Hearth can send this to: {name}’s sign-in is their new GX Mail address, which opens only once they have a Hearth password. Give them this link yourself (on paper, or on their phone). It works once, within 7 days, and lets whoever opens it choose the password, so don’t send it anywhere else.
        </p>
        <input className="input input--mono" readOnly value={link ?? ''} onFocus={(e) => e.currentTarget.select()} aria-label="Set-up link" />
        <Button icon="file" onClick={() => void navigator.clipboard?.writeText(link ?? '')}>
          Copy
        </Button>
      </div>
    </Dialog>
  );
}
